import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import TaskHistoryDto from '../../dtos/Task/TaskHistoryDto';
import TaskCommentButtonBar from './TaskCommentButtonBar';
import ConfirmDeleteDialog from '../Modal/ConfirmDelete';
import { FormLabel, Grid } from '@mui/material';
import EditCommentModal from '../Modal/EditCommentModal';
import TaskApiService from '../../api/TaskApiService';
import AuthUtil from '../Utils/AuthUtil';
import { useSelector } from 'react-redux';
import { useSnackbar } from '../Common/SnackbarProvider';

export default function TaskComment(props) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const { taskChange, setTaskChange, handleDeleted } = props;
    const[showDeleteDialog, setShowDeleteDialog] = useState(false);
    const[showEditDialog, setShowEditDialog] = useState(false);

    const [originalValue, setOriginalValue] = useState('');

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isAdmin = useMemo(() => AuthUtil.isAdmin(userToken), [userToken]);

    const taskApiService = new TaskApiService();

    async function handleDeleteComment() {
        setShowDeleteDialog(true);
    }

    function handleEditComment() {
        setShowEditDialog(true);
    }

    async function handleSaveEdit() {
        const response = await taskApiService.updateTaskChange(taskChange.taskChangeId, taskChange);

        if(response && response.success) {
            snackbar.success(t('taskComment.toast.updated'));
            setShowEditDialog(false);
        } else {
            snackbar.error(t('taskComment.toast.updateError'));
        }
    }

    function handleCommentChange(value) {
        setTaskChange(value);
    }

    async function handleConfirmDelete() {
        const response = await taskApiService.deleteTaskChange(taskChange.taskChangeId);

        if(response && response.success) {
            snackbar.success(t('taskComment.toast.deleted'));
        } else {
            snackbar.error(t('taskComment.toast.deleteError'));
        }
        setShowDeleteDialog(false);
        handleDeleted();
    }

    function handleEditCancel() {
        setTaskChange(originalValue);
        setShowEditDialog(false);
    }

    useEffect(() => {
        setOriginalValue(`${taskChange.taskChange}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return(
        <Box
            sx={{
                width: '100%',
                border: '1px solid #ddd',
                margin: '10px',
                backgroundColor: 'white',
                padding: '10px',
                display: 'flex',
            }
            }>
            <Grid container>
                <Grid item xs={10}>
                    <FormLabel
                        style={{ fontWeight: 'bold' }}>
                        {t('taskHistory.postedBy', { type: taskChange?.taskChangeType ?? '', user: taskChange?.taskChangeUser ?? '', time: taskChange?.taskChangeTime ?? '' })}
                    </FormLabel>
                </Grid>
                {isAdmin && taskChange?.taskChangeType === 'comment' &&
                    <Grid item xs={2}>
                        <TaskCommentButtonBar handleEditComment={handleEditComment} handleDeleteComment={handleDeleteComment} style={{marginRight: '2px', marginLeft: 'auto'}} />
                    </Grid>
                }
                <Grid item xs={12}>
                    {/*
                      * Box, not FormLabel: FormLabel always renders {children} alongside its
                      * required-asterisk node, and two JSX expression children make an array --
                      * non-null even when both are undefined -- which React rejects next to
                      * dangerouslySetInnerHTML ("Can only set one of children or
                      * props.dangerouslySetInnerHTML"). A div is also the right element for
                      * rendered comment body text; the old reactstrap Label was a <label>.
                      */}
                    <Box dangerouslySetInnerHTML={{ __html: taskChange.taskChange }} />
                </Grid>
            </Grid>

            <EditCommentModal
                open={showEditDialog}
                cancel={handleEditCancel}
                comment={taskChange.taskChange}
                setComment={handleCommentChange}
                save={handleSaveEdit}
                originalValue={originalValue}
                useEditor={true}
            />
            <ConfirmDeleteDialog
                onClose={handleConfirmDelete}
                open={showDeleteDialog}
                title={t('taskComment.confirmDeleteTitle')}
                content={t('taskComment.confirmDeleteContent')}
            />
        </Box>
    )
}

TaskComment.propTypes = {
    taskChange: PropTypes.objectOf(TaskHistoryDto)
}
